<template>
  <SectionContainer
    class="mt-5"
    title-css-classes="text-center text-secondary mb-2"
  >
    <AppHead />
    <h3 class="text-center text-secondary mb-2">
      {{ pageSubTitle }}
    </h3>
    <h3 class="text-center text-secondary">{{ surveyTitle }}</h3>
    <div class="d-flex py-3">
      <button
        class="mx-auto btn btn-sm btn-primary py-2 px-3"
        style="font-size: 0.8rem"
        @click="exportToPdf()"
      >
        <span class="pe-2">Exporter le rapport en PDF</span>
        <AppIcon icon="pdf" />
      </button>
    </div>
    <p class="text-center text-400">
      Pour accéder à un chapitre, une section ou un thème donné, cliquez sur
      l'entête correspondante.
    </p>
    <div class="survey row text-uppercase">
      <ChapterBar
        v-for="(chapter, chapterKey, index) in chapters"
        :key="`chapter-${index}`"
        :chapter="chapter"
        :chapter-selection="chapterSelectionMode"
        @clicked="loadForm"
        @selectChapter="updateChapterSelection(chapter.id)"
      />
      <EditSurveyChapterSelection
        :form="chapterSelectionForm"
        :is-updated="isChapterSelectionUpdated"
        :chapter-selection-mode="chapterSelectionMode"
        @click="toggleEditChapterSelection"
        @cancel="cancelChapterSelectionEdit"
        @save="saveChapterSelection"
      />
    </div>
    <GuestNotification />
  </SectionContainer>
</template>

<script>
import Vue from 'vue'
import { isEqual } from 'lodash'
import App from '../Layouts/App'
import ChapterBar from '../Components/ChapterBar.vue'
import GuestNotification from '../Components/GuestNotification.vue'
import EditSurveyChapterSelection from '../Components/EditSurveyChapterSelection.vue'
import axios from 'axios'
import { saveAs } from 'file-saver'

export default {
  components: {
    ChapterBar,
    GuestNotification,
    EditSurveyChapterSelection,
  },
  layout: App,
  props: {
    survey: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      pageSubTitle: 'Sommaire et résultats',
      chapterSelectionMode: false,
      surveyChapterIds: [...Object.values(this.$page.props.survey.chapter_ids)],
    }
  },
  computed: {
    chapterSelectionForm() {
      return this.$inertia.form({
        chapter_ids: this.surveyChapterIds,
      })
    },
    isChapterSelectionUpdated() {
      const existingSelection = [
        ...Object.values(this.$page.props.survey.chapter_ids),
      ]
      const newSelection = [...this.surveyChapterIds]

      return !isEqual(existingSelection, newSelection)
    },
    chapters() {
      return this.survey.chapters.reduce((chapters, chapter) => {
        let selected = false
        if (this.surveyChapterIds.includes(chapter.id)) {
          selected = true
        }
        Vue.set(chapter, 'selected', selected)
        chapters[chapter.id] = chapter
        return chapters
      }, {})
    },
    surveyName() {
      return this.survey.name ? `${this.survey.name}` : `questionnaire`
    },
    surveyTitle() {
      return `Bilan de ${this.surveyName.toLowerCase()} - ${this.$date(
        this.survey.created_at
      ).format('ll')}`
    },
    questionCount() {
      return this.survey.questions_count
    },
    maxScore() {
      return this.questionCount * 10
    },
  },
  methods: {
    toggleEditChapterSelection() {
      this.chapterSelectionMode = !this.chapterSelectionMode
    },
    loadForm(surveyLevel) {
      return this.$inertia.visit(
        this.$route('survey.user.show', {
          id: this.survey.id,
        }),
        {
          data: this.getSurveyLevelQuery(surveyLevel),
        }
      )
    },
    updateChapterSelection(chapterId) {
      this.surveyChapterIds = [...new Set(this.surveyChapterIds)]
      const index = this.surveyChapterIds.indexOf(chapterId)
      if (index === -1) {
        this.surveyChapterIds.push(chapterId)
      } else {
        Vue.delete(this.surveyChapterIds, index)
      }
    },
    cancelChapterSelectionEdit() {
      this.surveyChapterIds = [
        ...Object.values(this.$page.props.survey.chapter_ids),
      ]
      this.toggleEditChapterSelection()
    },
    saveChapterSelection() {
      this.$toasted.global.saving()
      this.chapterSelectionForm.put(
        this.$route('survey.user.updateChapters', this.survey.id),
        {
          onSuccess: () => {
            this.$toasted.global.saved()
            this.toggleEditChapterSelection()
          },
          onError: (error) => {
            console.error(error)
            this.$toasted.global.savingError()
          },
        }
      )
    },
    getSurveyLevelQuery(surveyLevel) {
      if (!surveyLevel) {
        return {}
      }

      const levelQuery = {}
      if (!surveyLevel.get('chapterId')) {
        return levelQuery
      }
      levelQuery.chapitre = surveyLevel.get('chapterId')

      if (!surveyLevel.get('sectionId')) {
        return levelQuery
      }
      levelQuery.section = surveyLevel.get('sectionId')

      if (!surveyLevel.get('themeId')) {
        return levelQuery
      }
      levelQuery.theme = surveyLevel.get('themeId')

      return levelQuery
    },
    async exportToPdf() {
      this.$toasted.global.exporting()

      try {
        await this.getPdfFile()
        this.$toasted.global.exported()
      } catch (error) {
        this.$toasted.global.exportingError()
        console.error(error)
      }
    },
    async getPdfFile() {
      const response = await axios({
        url: this.$route('survey.user.exportToPdf', this.survey.id),
        data: [],
        method: 'POST',
        responseType: 'blob',
      })
      const header = response.headers['content-disposition']
      const filename = header
        .split(';')[1]
        .split('=')[1]
        .replace('"', '')
        .replace('"', '')

      return saveAs(new Blob([response.data]), filename)
    },
  },
}
</script>
<style scoped>
h2,
h3 {
  font-style: normal;
  font-weight: 400;
  line-height: 1.4em;
  font-size: 1.4rem;
  letter-spacing: 0.108em;
}
h3 {
  font-size: 1.2rem;
}
</style>

